import React from 'react';
import { Box, Link, Skeleton, Typography, useTheme } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import { FactorRangeRow } from './FactorRangeRow';
import { getDomain, toPercent } from './factorScale';
import { getFactorRangePalette, rem } from './factorRangePalette';
import { FACTOR_RANGE_GRID } from './factorRangeLayout';
import {
  TILE_SUBTITLE_FONT_SIZE,
  TILE_SUBTITLE_GAP,
  TILE_TITLE_FONT_SIZE,
  TILE_TITLE_FONT_WEIGHT,
  fullBleed,
} from '../../panels/panelLayout';
import type {
  FactorRangeAggregate,
  FactorRangeDatum,
} from './factorRange.types';

interface CowbellFactorsRangePanelProps {
  readonly factors: readonly FactorRangeDatum[];
  readonly aggregate: FactorRangeAggregate | null;
  readonly companyName?: string;
  /** Human-readable "as of" text for the underlying risk scan. */
  readonly updatedAt?: string;
  /** Describes the period the trend deltas were measured over. */
  readonly trendWindowLabel?: string;
  readonly isLoading?: boolean;
  readonly title?: string;
  /** Omitted by default rather than rendering a link that goes nowhere. */
  readonly scoringHelpHref?: string;
}

/**
 * Cowbell Factors drawn against a single shared axis, so every factor is
 * comparable to every other at a glance, with the company aggregate promoted
 * into the header. Presentation only -- see the container for data.
 */
export function CowbellFactorsRangePanel({
  factors,
  aggregate,
  companyName,
  updatedAt,
  trendWindowLabel,
  isLoading = false,
  title = 'Cowbell Factors',
  scoringHelpHref,
}: Readonly<CowbellFactorsRangePanelProps>) {
  const { palette: themePalette } = useTheme();
  const palette = getFactorRangePalette(themePalette.mode);
  const domain = getDomain(factors);
  const behindCount = factors.filter(
    (factor) => factor.company < factor.industry
  ).length;

  if (isLoading) {
    return <PanelSkeleton title={title} />;
  }

  if (!factors.length && !aggregate) {
    return (
      <Box data-qa="COWBELL_FACTORS_PANEL">
        <PanelHeading title={title} />
        <Box textAlign="center" sx={{ paddingY: rem(24) }}>
          <Typography
            sx={{ fontSize: TILE_SUBTITLE_FONT_SIZE, color: palette.inkFaint }}
          >
            No Cowbell Factors are available for this account.
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box data-qa="COWBELL_FACTORS_PANEL">
      {/* Top-aligned: bottom alignment let the short "COMPANY AGGREGATE" label
          ride above the panel title, so the two halves of the header read as
          if they began on different lines. */}
      <Box
        display="flex"
        flexWrap="wrap"
        alignItems="flex-start"
        justifyContent="space-between"
        gap={rem(24)}
        sx={{ paddingBottom: rem(14) }}
      >
        <Box>
          <PanelHeading title={title} />
          <Typography
            sx={{
              maxWidth: rem(430),
              marginTop: TILE_SUBTITLE_GAP,
              fontSize: TILE_SUBTITLE_FONT_SIZE,
              lineHeight: 1.6,
              color: palette.inkFaint,
            }}
          >
            {buildSummary({ count: factors.length, companyName, updatedAt })}
          </Typography>
        </Box>

        {aggregate ? (
          <AggregateSummary
            aggregate={aggregate}
            trendWindowLabel={trendWindowLabel}
          />
        ) : null}
      </Box>

      <Box
        data-qa="COWBELL_FACTORS_LEGEND"
        display="flex"
        flexWrap="wrap"
        alignItems="center"
        gap={rem(24)}
        sx={{
          ...fullBleed,
          borderTop: `1px solid ${palette.line}`,
          borderBottom: `1px solid ${palette.line}`,
          backgroundColor: palette.surfaceMuted,
          paddingTop: rem(8),
          paddingBottom: rem(8),
        }}
      >
        <LegendItem label="Your score">
          <Box
            width={rem(9)}
            height={rem(9)}
            borderRadius="50%"
            bgcolor={palette.brand}
          />
        </LegendItem>
        <LegendItem label="Industry average">
          <Box width={rem(2)} height={rem(11)} bgcolor={palette.reference} />
        </LegendItem>
        <LegendItem label="Industry range (min–max)">
          <Box
            width={rem(20)}
            height={rem(4)}
            borderRadius={rem(2)}
            bgcolor={palette.band}
          />
        </LegendItem>
        <LegendItem label="Below industry average">
          <Box
            width={rem(9)}
            height={rem(9)}
            borderRadius="50%"
            bgcolor={palette.critical}
          />
        </LegendItem>
        {behindCount > 0 && (
          <Typography
            sx={{
              marginLeft: 'auto',
              fontSize: rem(11),
              fontWeight: 500,
              color: palette.inkMuted,
            }}
          >
            {behindCount} factor{behindCount === 1 ? '' : 's'} below industry
            average
          </Typography>
        )}
      </Box>

      <Box
        aria-hidden="true"
        sx={{
          display: { xs: 'none', sm: 'grid' },
          columnGap: rem(24),
          gridTemplateColumns: FACTOR_RANGE_GRID,
          paddingTop: rem(10),
        }}
      >
        <span />
        <Box position="relative" height={rem(14)}>
          {domain.ticks.map((tick) => (
            <Typography
              key={tick}
              sx={{
                position: 'absolute',
                left: `${toPercent(tick, domain)}%`,
                transform: 'translateX(-50%)',
                fontSize: rem(10),
                fontVariantNumeric: 'tabular-nums',
                color: palette.inkFaint,
              }}
            >
              {tick}
            </Typography>
          ))}
        </Box>
        <span />
      </Box>

      <Box component="ul" sx={{ listStyle: 'none', margin: 0, padding: 0 }}>
        {factors.map((factor) => (
          <FactorRangeRow
            key={factor.factorKey}
            factor={factor}
            domain={domain}
          />
        ))}
      </Box>

      <Box
        data-qa="COWBELL_FACTORS_FOOTER"
        display="flex"
        flexWrap="wrap"
        alignItems="center"
        gap={rem(12)}
        sx={{
          ...fullBleed,
          borderTop: `1px solid ${palette.line}`,
          paddingTop: rem(12),
          marginTop: rem(4),
        }}
      >
        <InfoOutlinedIcon sx={{ fontSize: rem(15), color: palette.inkFaint }} />
        <Typography sx={{ fontSize: rem(12.5), color: palette.inkMuted }}>
          Factors are rescanned daily and rescored weekly. Ranges reflect
          insureds in your industry and revenue band.
        </Typography>
        {scoringHelpHref ? (
          <Link
            href={scoringHelpHref}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              marginLeft: 'auto',
              fontSize: rem(12.5),
              fontWeight: 600,
              color: palette.brand,
              textDecoration: 'none',
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            How factors are scored
          </Link>
        ) : null}
      </Box>
    </Box>
  );
}

function PanelHeading({ title }: Readonly<{ title: string }>) {
  const { palette: themePalette } = useTheme();
  const palette = getFactorRangePalette(themePalette.mode);

  return (
    <Typography
      component="h3"
      sx={{
        margin: 0,
        fontSize: TILE_TITLE_FONT_SIZE,
        fontWeight: TILE_TITLE_FONT_WEIGHT,
        lineHeight: 1.3,
        color: palette.ink,
      }}
    >
      {title}
    </Typography>
  );
}

function LegendItem({
  label,
  children,
}: Readonly<{ label: string; children: React.ReactNode }>) {
  const { palette: themePalette } = useTheme();
  const palette = getFactorRangePalette(themePalette.mode);

  return (
    <Box display="flex" alignItems="center" gap={rem(6)}>
      {children}
      <Typography sx={{ fontSize: rem(11), color: palette.inkMuted }}>
        {label}
      </Typography>
    </Box>
  );
}

function AggregateSummary({
  aggregate,
  trendWindowLabel,
}: Readonly<{
  aggregate: FactorRangeAggregate;
  trendWindowLabel?: string;
}>) {
  const { palette: themePalette } = useTheme();
  const palette = getFactorRangePalette(themePalette.mode);
  const trend = aggregate.trend;
  // A zero delta says nothing a reader needs; "0 pts" next to the score reads
  // as a measurement rather than the absence of movement.
  const showTrend = typeof trend === 'number' && trend !== 0;
  const trendColor = (trend ?? 0) < 0 ? palette.critical : palette.positive;

  return (
    <Box textAlign="right" data-qa="COWBELL_FACTORS_AGGREGATE">
      <Typography
        sx={{
          fontSize: rem(12),
          fontWeight: 500,
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
          color: palette.inkFaint,
        }}
      >
        Company aggregate
      </Typography>
      {/* The score stands alone: a Cowbell Factor is not scored against a
          fixed total, so a denominator here would assert a scale that does
          not exist. */}
      <Typography
        component="p"
        sx={{
          marginTop: rem(4),
          fontSize: rem(40),
          fontWeight: 600,
          lineHeight: 1,
          letterSpacing: '-0.02em',
          fontVariantNumeric: 'tabular-nums',
          color: palette.ink,
        }}
      >
        {aggregate.company}
      </Typography>
      <Typography
        sx={{
          display: 'block',
          marginTop: rem(8),
          fontSize: rem(13),
          color: palette.inkMuted,
        }}
      >
        {showTrend && (
          <Typography
            component="span"
            sx={{ fontSize: rem(13), fontWeight: 600, color: trendColor }}
          >
            {`${formatSignedPoints(trend as number)}${
              trendWindowLabel ? ` ${trendWindowLabel}` : ''
            } · `}
          </Typography>
        )}
        {`industry ${aggregate.industry} (${aggregate.min}–${aggregate.max})`}
      </Typography>
    </Box>
  );
}

function PanelSkeleton({ title }: Readonly<{ title: string }>) {
  return (
    <Box data-qa="COWBELL_FACTORS_PANEL_LOADING">
      <PanelHeading title={title} />
      {Array.from({ length: 8 }).map((_unused, index) => (
        <Box
          key={`cf-range-skeleton-${index}`}
          display="grid"
          gridTemplateColumns={FACTOR_RANGE_GRID}
          alignItems="center"
          gap={rem(24)}
          sx={{ padding: `${rem(10)} 0` }}
        >
          <Skeleton variant="text" width="80%" />
          <Skeleton variant="rectangular" sx={{ height: rem(8) }} />
          <Skeleton variant="text" width="60%" />
        </Box>
      ))}
    </Box>
  );
}

/** "+3 pts" / "-2 pts"; a leading sign reads better than a bare number here. */
const formatSignedPoints = (trend: number) => {
  const sign = trend > 0 ? '+' : '';
  return `${sign}${trend} pts`;
};

const buildSummary = ({
  count,
  companyName,
  updatedAt,
}: {
  count: number;
  companyName?: string;
  updatedAt?: string;
}) => {
  const owner = companyName ? `${companyName}'s` : 'your';
  const updated = updatedAt ? ` Updated ${updatedAt}.` : '';
  const dimensions = count === 1 ? 'dimension' : 'dimensions';
  const comparison = count === 1 ? 'compared' : 'each compared';
  return `${count} scored ${dimensions} of ${owner} cyber risk, ${comparison} against the range and average for your industry.${updated}`;
};

export default CowbellFactorsRangePanel;
